// Mirrors the backend worktime-stats DTOs (API.Core/Models/Company/WorktimeStats.cs) and the
// WorktimeActivityFilter enum (API.Core/Enums/Company/WorktimeActivityFilter.cs).

import {EmploymentRelationshipNl} from '../x-models/employment-relationship-nl';

/** Roster scope by employment status — sent verbatim as the `activity` query parameter (bound by name server-side). */
export type WorktimeActivity = 'All' | 'Active' | 'Inactive';

/**
 * Client-side mirror of `WorktimeStatsCalculator.RoundHours` (C#): two decimals, midpoint **away from zero**
 * (kaufmännische Rundung) — `2.345 → 2.35`, `-2.345 → -2.35`, never banker's `2.34`.
 *
 * Every hour figure the backend sends is already rounded this way; this exists solely for the figures the
 * component RE-AGGREGATES client-side (see `EmployeesComponent.summary`), so those land on the same grid.
 *
 * Why the string/exponent shift rather than the obvious `Math.round(v * 100) / 100`: multiplying by 100 first
 * drags exact-looking midpoints off the midpoint — `2.345 * 100` is `234.49999999999997`, which rounds *down*
 * to `2.34`. Re-parsing through the decimal exponent (`'2.345e2'` → `234.5`) uses the shortest round-trip
 * decimal string, so the midpoint survives. `Math.round` is half-up, so taking `Math.abs` first and reapplying
 * the sign afterwards turns it into away-from-zero.
 */
export function roundHours(value: number): number {
  if (!Number.isFinite(value)) return value;
  const magnitude = Math.abs(value);
  const asString = `${magnitude}`;
  // Values already in exponential notation ('1e-7') can't take the 'e2' suffix — they're far below the
  // rounding grid anyway, so the plain multiply is exact enough there.
  const rounded = asString.includes('e') || asString.includes('E')
    ? Math.round(magnitude * 100) / 100
    : Number(`${Math.round(Number(`${asString}e2`))}e-2`);
  const signed = (value < 0 ? -1 : 1) * rounded;
  // A small negative (-0.001) rounds to 0 and the sign multiply would hand back -0, which renders as "-0,00".
  // `signed === 0` is true for -0, so this normalizes it to a plain zero.
  return signed === 0 ? 0 : signed;
}

/** Per-employee worktime metrics for the requested range. */
export interface WorktimeStatsDto {
  employeeId: string;
  employeeName: string;
  /** Whether the employee is currently employed (joined in the past, not yet left). */
  isActive: boolean;
  joined: string;
  left: string;
  hoursWorked: number;
  shiftCount: number;
  firstShift?: string | null;
  lastShift?: string | null;
  nextShift?: string | null;
  averageHoursPerMonth: number;
  averageShiftLength: number;
  employmentRelationship?: EmploymentRelationshipNl | null;
}

/** The company-wide overview: per-employee rows plus sum/avg aggregates for the range. */
export interface WorktimeStatsOverviewDto {
  start: string;
  end: string;
  employeeCount: number;
  totalHours: number;
  totalShiftCount: number;
  averageHoursPerEmployee: number;
  averageHoursPerMonth: number;
  employees: WorktimeStatsDto[];
}

/** Optional filters for a worktime-stats request. */
export interface WorktimeStatsQuery {
  /** Inclusive start calendar date (yyyy-MM-dd); the backend interprets it in the company time zone. */
  startDate?: string;
  /** Inclusive end calendar date (yyyy-MM-dd); the backend interprets it in the company time zone. */
  endDate?: string;
  activity: WorktimeActivity;
  employmentRelationshipIds?: string[];
  employeeIds?: string[];
}
